import { apiService } from './api';

/**
 * Sections Complete Interface
 * Tracks completion status of all editor sections
 */
export interface SectionsComplete {
  game: boolean;
  assets: boolean;
  description: boolean;
  review: boolean;
}

/**
 * Project Metadata Interface
 * Editor state and completion flags
 */
interface ProjectMetadata {
  sectionsComplete: SectionsComplete;
  lastEditedSection?: string;
  customSettings?: Record<string, any>;
  tags?: string[];
  sdkVersion?: string; // SDK version used for this project
  [key: string]: any;
}

/**
 * Project Details Interface
 * Game-specific configuration
 */
interface ProjectDetails {
  categories?: string[];
  playInstructions?: string;
  iconUrl?: string;
  bannerUrl?: string;
  category?: string;
  minPlayers?: number;
  maxPlayers?: number;
  duration?: number;
  difficulty?: string;
  instructions?: string;
  rules?: string[];
  customFields?: Record<string, any>;
  [key: string]: any;
}

/**
 * Project Data Interface
 * Complete project data including metadata and details
 */
export interface ProjectData {
  dataId: string;
  projectId: string;
  userId: string;
  name?: string;
  description?: string;
  projectDetails: ProjectDetails;
  metadata: ProjectMetadata;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * ProjectDataService
 * Manages project-level data including completion flags and metadata
 */
class ProjectDataService {
  /**
   * Get project data
   */
  async getProjectData(projectId: string): Promise<ProjectData> {
    const response = await apiService.get('/editor/project-data', {
      params: { projectId }
    });
    return response.data.data;
  }

  /**
   * Set a section's completion status (only "assets" can be force-completed)
   */
  async setSectionComplete(projectId: string, section: 'assets', value: boolean): Promise<void> {
    await apiService.put('/editor/project-data/section-complete', {
      projectId,
      section,
      value
    });
  }

  /**
   * Get sections completion status
   */
  async getSectionsComplete(projectId: string): Promise<SectionsComplete> {
    const projectData = await this.getProjectData(projectId);
    return projectData.metadata.sectionsComplete;
  }

  /**
   * Update project details (title, description, categories, play instructions, images)
   * Now uses the game_content endpoint directly (same as Studio)
   */
  async updateProjectDetails(
    projectId: string,
    details: {
      title: string;
      categories: string[];
      description: string;
      playInstructions: string;
      iconUrl?: string;
      bannerUrl?: string;
      screenshots?: string[];
    }
  ): Promise<any> {
    // Map to game_content format
    const response = await apiService.put(`/games/${projectId}/content`, {
      name: details.title,
      description: details.description,
      category: details.categories[0]?.toLowerCase(), // First category as primary, lowercase
      tags: details.categories, // All categories as tags
      play_instructions: details.playInstructions,
      icon_url: details.iconUrl,
      cover_image_url: details.bannerUrl,
      screenshot_urls: details.screenshots || []
    });
    return response.data;
  }

  /**
   * Update project metadata
   */
  async updateProjectMetadata(projectId: string, metadata: Partial<ProjectMetadata>): Promise<ProjectData> {
    const response = await apiService.put('/editor/project-data/metadata', {
      projectId,
      metadata
    });
    return response.data.data;
  }
}

export default new ProjectDataService();

